<template>
  <validation-observer>
    <b-form slot-scope="{ validate }" @submit.prevent="validate().then(onSubmit)">
      <!-- Sticky Form Header -->
      <div
        class="px-2 rounded-sm resource-header border-bottom d-flex justify-content-end align-items-start mb-1 sticky-top bg-white"
        style="z-index: 1000; padding: 1rem 0"
      >
        <div class="actions-container d-flex justify-content-between w-100">
          <div>
            <h4 class="mb-0">
              {{ formData && formData.id ? 'Edit Customer' : 'Add New Customer' }}
            </h4>
            <small class="text-muted">{{
              formData && formData.id
                ? 'Update customer information'
                : 'Create a new customer record'
            }}</small>
          </div>
          <div>
            <b-button
              type="button"
              variant="outline-default"
              class="mr-1"
              :disabled="loading"
              @click="modalMode ? $emit('cancel') : $router.go(-1)"
            >
              Cancel
            </b-button>
            <b-button type="submit" variant="primary" :disabled="loading">
              <b-spinner small v-if="loading" class="mr-1"></b-spinner>
              {{
                loading
                  ? 'Saving...'
                  : formData && formData.id
                    ? 'Update Customer'
                    : 'Save Customer'
              }}
            </b-button>
          </div>
        </div>
      </div>

      <!-- Required Information Card -->
      <b-card no-body class="mt-2">
        <b-card-header>
          <b-card-title>Required Information</b-card-title>
        </b-card-header>

        <b-card-body>
          <b-row>
            <!-- Name -->
            <b-col md="6">
              <form-input
                v-model="customerFormData.name"
                :disabled="loading"
                label="Name"
                name="Name"
                rules="required"
                placeholder="Enter customer name"
              />
            </b-col>

            <!-- NTN Number -->
            <b-col md="6">
              <form-input
                v-model="customerFormData.ntnNumber"
                :disabled="loading"
                label="NTN Number"
                name="NTN Number"
                placeholder="Enter NTN number (1234567, 1234567-8, or F123456-7)"
                v-mask="ntnMask"
                vid="ntn"
                rules="customerNtn:cnic"
                @input="handleNtnInput"
                @blur="handleNtnBlur"
              />
            </b-col>

            <!-- CNIC Number -->
            <b-col md="6">
              <form-input
                v-model="customerFormData.cnicNumber"
                :disabled="loading"
                label="CNIC Number"
                name="CNIC Number"
                placeholder="Enter CNIC number (13 digits)"
                v-mask="'#####-#######-#'"
                vid="cnic"
                rules="customerCnic:ntn"
              />
            </b-col>

            <!-- STRN Number -->
            <b-col md="6">
              <form-input
                v-model="customerFormData.gstNumber"
                :disabled="loading"
                label="STRN Number"
                name="STRN Number"
                placeholder="Enter STRN number"
                v-mask="'##-##-####-###-##'"
                @input="handleGstInput"
              />
            </b-col>

            <!-- Category -->
            <b-col md="6">
              <form-select
                v-model="customerFormData.category"
                label="Category"
                name="Category"
                rules="required"
                :options="categoryOptions"
              />
            </b-col>

            <!-- Sub Category -->
            <b-col md="6">
              <sub-category-dropdown-field
                v-model="customerFormData.subCategoryID"
                :disabled="customerFormData.category.length === 0"
                :filter="customerFormData.category"
                label="Category"
                name="Category"
                :rules="''"
              />
            </b-col>

            <!-- Province -->
            <b-col md="6">
              <form-select
                v-model="customerFormData.province"
                :disabled="loading"
                label="Province"
                name="Province"
                rules="required"
                :options="provinceOptions"
              />
            </b-col>

            <!-- Address -->
            <b-col md="12">
              <form-text-area
                v-model="customerFormData.address"
                :disabled="loading"
                label="Address"
                name="Address"
                placeholder="Enter address"
                rules="required"
              />
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <!-- Contact Information Card -->
      <b-card no-body>
        <b-card-header>
          <b-card-title>Contact Information</b-card-title>
        </b-card-header>

        <b-card-body>
          <b-row>
            <!-- Short Name -->
            <b-col md="6">
              <form-input
                v-model="customerFormData.shortName"
                :disabled="loading"
                label="Short Name"
                name="Short Name"
                placeholder="Enter short name"
              />
            </b-col>

            <!-- Contact Person -->
            <b-col md="6">
              <form-input
                v-model="customerFormData.contactPerson"
                :disabled="loading"
                label="Contact Person"
                name="Contact Person"
                placeholder="Enter contact person"
              />
            </b-col>

            <!-- Email -->
            <b-col md="6">
              <form-input
                v-model="customerFormData.email"
                :disabled="loading"
                label="Email"
                name="Email"
                rules="email"
                type="email"
                placeholder="Enter email address"
              />
            </b-col>

            <!-- Cell Number -->
            <b-col md="6">
              <form-input
                v-model="customerFormData.cellNumber"
                :disabled="loading"
                label="Cell Number"
                name="Cell Number"
                placeholder="Enter cell number (+92##########)"
                rules="pakistaniPhone"
                v-mask="'+92##########'"
              />
            </b-col>

            <!-- Whatsapp -->
            <b-col md="6">
              <form-input
                v-model="customerFormData.whatsappOrUAN"
                :disabled="loading"
                label="Whatsapp"
                name="Whatsapp"
                placeholder="Enter whatsapp number (+92##########)"
                v-mask="'+92##########'"
                rules="pakistaniPhone"
              />
            </b-col>

            <!-- Website -->
            <b-col md="6">
              <form-input
                v-model="customerFormData.website"
                :disabled="loading"
                label="Website"
                name="Website"
                placeholder="Enter website URL"
              />
            </b-col>

            <!-- Commission Percent -->
            <b-col md="6">
              <form-input
                v-model.number="customerFormData.commissionPercent"
                :disabled="loading"
                label="Commission %"
                name="Commission %"
                type="number"
                min="0"
                step="0.01"
                placeholder="Enter commission percentage"
              />
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <!-- Status Card -->
      <b-card no-body>
        <b-card-header>
          <b-card-title>Status</b-card-title>
        </b-card-header>

        <b-card-body>
          <b-row>
            <b-col md="12">
              <b-form-group>
                <b-form-checkbox
                  id="customer-active"
                  v-model="customerFormData.isActive"
                  name="customer-active"
                  value="true"
                  unchecked-value="false"
                  :disabled="loading"
                >
                  Active
                </b-form-checkbox>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>
    </b-form>
  </validation-observer>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BForm,
  BFormGroup,
  BFormSelect,
  BFormCheckbox,
  BFormInvalidFeedback,
  BButton,
  BSpinner,
} from 'bootstrap-vue';
import { Validator, ValidationProvider, ValidationObserver } from 'vee-validate';
import { ref, computed, watch, onUnmounted } from '@vue/composition-api';
import { FormInput, FormTextArea, FormSelect } from '@/views/apps/fbr-invoice/components/form';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import SubCategoryDropdownField from '../components/form/SubCategoryDropdownField.vue';
import SubCategoryStoreModule from '../sub-categories/SubCategoryStoreModule';
import store from '@/store';

Validator.extend(
  'customerCnic',
  (cnic, [ntn]) => {
    console.log('ntnOrCnic VALIDATION:', { cnic, ntn });

    // Check if current field has a cnic
    const hasCnic = !!cnic && cnic.trim().length > 0;

    // Check if other field has a cnic
    const hasNtn = !!ntn && ntn.trim().length > 0;

    console.log('NTN OR CNIC VALIDATION:', { hasCnic, hasNtn });

    const cleanedValue = cnic.replace(/\D/g, '');
    if (!(hasCnic && /^\d{13}$/.test(cleanedValue))) {
      return false;
    }

    // At least one of them must have a cnic
    return hasCnic || hasNtn;
  },
  {
    hasTarget: true,
  }
);
Validator.extend(
  'customerNtn',
  (ntn, [cnic]) => {
    console.log('ntnOrCnic VALIDATION:', { ntn, cnic });

    // Check if current field has a ntn
    const hasNtn = !!ntn && ntn.trim().length > 0;

    // Check if other field has a ntn
    const hasCnic = !!cnic && cnic.trim().length > 0;

    console.log('NTN OR CNIC VALIDATION:', { hasNtn, hasCnic });

    // If NTN field is empty, check if CNIC is provided (at least one required)
    if (!hasNtn) {
      return hasCnic;
    }

    // Validate NTN format
    const ntnValue = ntn.trim();

    // Format 1: 7 digits only (1234567)
    if (/^\d{7}$/.test(ntnValue)) {
      return true;
    }

    // Format 2: 8 digits with dash (1234567-8)
    if (/^\d{7}-\d{1}$/.test(ntnValue)) {
      return true;
    }

    // Format 3: Letter + 6 digits + dash + 1 digit (F123456-7)
    if (/^[A-Za-z]\d{6}-\d{1}$/.test(ntnValue)) {
      return true;
    }

    // Format 4: Letter + 6 digits (C955166)
    if (/^[A-Za-z]\d{6}$/.test(ntnValue)) {
      return true;
    }

    // If NTN format is invalid, return false
    return false;
  },
  {
    hasTarget: true,
    getMessage: (field) =>
      `${field} must be 7 digits (1234567), 8 digits with dash (1234567-8), letter format with dash (F123456-7), or letter format without dash (C955166)`,
  }
);

export default {
  name: 'CustomerForm',
  emits: ['submit', 'update:form-data', 'cancel'],
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormSelect,
    BFormCheckbox,
    BFormInvalidFeedback,
    BButton,
    BSpinner,
    ToastificationContent,

    // Form Validation
    ValidationProvider,
    ValidationObserver,

    // Custom Components
    FormInput,
    FormTextArea,
    FormSelect,
    SubCategoryDropdownField,
  },
  props: {
    formData: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    modalMode: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const SUB_CATEGORY_APP_STORE_MODULE_NAME = 'sub-category';

    // Register module
    if (!store.hasModule(SUB_CATEGORY_APP_STORE_MODULE_NAME))
      store.registerModule(SUB_CATEGORY_APP_STORE_MODULE_NAME, SubCategoryStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(SUB_CATEGORY_APP_STORE_MODULE_NAME))
        store.unregisterModule(SUB_CATEGORY_APP_STORE_MODULE_NAME);
    });

    const toast = useToast();

    const categoryOptions = [
      { value: 'Registered', text: 'Registered' },
      { value: 'Unregistered', text: 'Unregistered' },
    ];

    const provinceOptions = [
      { value: 'Punjab', text: 'Punjab' },
      { value: 'Sindh', text: 'Sindh' },
      { value: 'Khyber Pakhtunkhwa', text: 'Khyber Pakhtunkhwa' },
      { value: 'Balochistan', text: 'Balochistan' },
      { value: 'Gilgit-Baltistan', text: 'Gilgit-Baltistan' },
    ];

    const blankCustomerData = {
      id: 0,
      name: '',
      shortName: '',
      cellNumber: '',
      category: '',
      subCategoryID: '',
      email: '',
      ntnNumber: '',
      gstNumber: '',
      address: '',
      province: '',
      cnicNumber: '',
      contactPerson: '',
      whatsappOrUAN: '',
      website: '',
      commissionPercent: 0,
      isActive: true,
    };

    const customerFormData = ref(
      props.formData ? props.formData : JSON.parse(JSON.stringify(blankCustomerData))
    );

    const handleGstInput = (value) => {
      // Auto-set category to 'Registered' when GST number is provided
      if (value && value.trim().length > 0 && value !== 'Registered') {
        customerFormData.value.category = 'Registered';
      } else {
        customerFormData.value.category = '';
      }
    };

    // Dynamic NTN mask based on input
    const ntnMask = computed(() => {
      const ntnValue = customerFormData.value.ntnNumber || '';
      // If starts with alphabet, use full format (A123456-7)
      if (ntnValue.length > 0 && /^[A-Za-z]/.test(ntnValue)) {
        return 'A######-#';
      }
      // If starts with digit or empty, use 8 digits with dash (1234567-8)
      return '#######-#';
    });

    const handleNtnInput = (value) => {
      // Clean the input value
      let cleanValue = value.replace(/[^A-Za-z0-9-]/g, '');

      if (cleanValue.length > 0) {
        // If starts with alphabet
        if (/^[A-Za-z]/.test(cleanValue)) {
          // Format: F123456-7 (letter + 6 digits + dash + 1 digit)
          const letterPart = cleanValue.substring(0, 1).toUpperCase();
          const numberPart = cleanValue.substring(1).replace(/[^0-9-]/g, '');

          if (numberPart.includes('-')) {
            // Handle existing dash, ensure proper format
            const parts = numberPart.split('-');
            if (parts.length === 2 && parts[0].length <= 6 && parts[1].length <= 1) {
              cleanValue = letterPart + parts[0] + '-' + parts[1];
            } else {
              // Remove dash and reformat
              const digitsOnly = numberPart.replace('-', '');
              if (digitsOnly.length >= 7) {
                cleanValue =
                  letterPart + digitsOnly.substring(0, 6) + '-' + digitsOnly.substring(6, 7);
              } else {
                cleanValue = letterPart + digitsOnly;
              }
            }
          } else {
            // No dash present
            if (numberPart.length >= 7) {
              // Auto-add dash: A123456-7
              cleanValue =
                letterPart + numberPart.substring(0, 6) + '-' + numberPart.substring(6, 7);
            } else if (numberPart.length <= 6) {
              // Still typing, don't add dash yet
              cleanValue = letterPart + numberPart;
            } else {
              // Limit to 7 digits max
              cleanValue = letterPart + numberPart.substring(0, 7);
            }
          }

          // Ensure we don't exceed the maximum length (A123456-7 = 9 chars)
          if (cleanValue.length > 9) {
            cleanValue = cleanValue.substring(0, 9);
          }
        } else {
          // If starts with digit, handle 8 digits with dash format (1234567-8)
          const digitsOnly = cleanValue.replace(/[^0-9]/g, '');

          if (digitsOnly.length >= 8) {
            // Auto-add dash: 1234567-8
            cleanValue = digitsOnly.substring(0, 7) + '-' + digitsOnly.substring(7, 8);
          } else if (digitsOnly.length <= 7) {
            // Still typing, don't add dash yet
            cleanValue = digitsOnly;
          } else {
            // Limit to 8 digits max
            cleanValue = digitsOnly.substring(0, 8);
          }
        }
      }

      customerFormData.value.ntnNumber = cleanValue;
    };

    // Handle NTN blur to ensure proper formatting
    const handleNtnBlur = () => {
      customerFormData.value.ntnNumber = formatNtnForSubmission(customerFormData.value.ntnNumber);
    };

    // Watch for local data changes and emit updates
    watch(
      customerFormData,
      (newVal) => {
        console.log('Customer Form Data:', JSON.stringify(newVal, null, 2));

        emit('update:form-data', newVal);
      },
      { deep: true }
    );

    // Format NTN before submission
    const formatNtnForSubmission = (ntn) => {
      if (!ntn || ntn.trim().length === 0) return ntn;

      const cleanNtn = ntn.trim();

      // If starts with letter and has 7+ digits after letter
      if (/^[A-Za-z]/.test(cleanNtn)) {
        const letter = cleanNtn.substring(0, 1).toUpperCase();
        const digits = cleanNtn.substring(1).replace(/[^0-9]/g, '');

        // If we have exactly 7 digits, format as A123456-7
        if (digits.length === 7) {
          return letter + digits.substring(0, 6) + '-' + digits.substring(6);
        }
        // If we have 6 digits, it's incomplete - return as is
        else if (digits.length === 6) {
          return letter + digits;
        }
        // If already properly formatted, return as is
        else if (/^[A-Za-z]\d{6}-\d{1}$/.test(cleanNtn)) {
          return cleanNtn.toUpperCase();
        }
      }
      // If starts with digit, handle 8 digits with dash format
      else if (/^\d/.test(cleanNtn)) {
        const digits = cleanNtn.replace(/[^0-9]/g, '');

        // If we have exactly 8 digits, format as 1234567-8
        if (digits.length === 8) {
          return digits.substring(0, 7) + '-' + digits.substring(7);
        }
        // If we have 7 digits, it's the old format - return as is
        else if (digits.length === 7) {
          return digits;
        }
        // If already properly formatted, return as is
        else if (/^\d{7}-\d{1}$/.test(cleanNtn)) {
          return cleanNtn;
        }
      }

      return cleanNtn;
    };

    const onSubmit = () => {
      if (
        customerFormData.value.ntnNumber.trim().length === 0 &&
        customerFormData.value.cnicNumber.trim().length === 0
      ) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Please enter either NTN or CNIC numbers',
            variant: 'danger',
            icon: 'AlertTriangleIcon',
          },
        });
        return;
      }

      // Format NTN before submission
      customerFormData.value.ntnNumber = formatNtnForSubmission(customerFormData.value.ntnNumber);

      emit('submit');
    };

    return {
      customerFormData,
      categoryOptions,
      provinceOptions,
      onSubmit,
      handleGstInput,
      ntnMask,
      handleNtnInput,
      handleNtnBlur,
    };
  },
};
</script>

<style lang="scss" scoped>
.b-card {
  margin-bottom: 1rem;
}

// Sticky header styling
.resource-header {
  background: white;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .actions-container {
    width: 100%;

    h4 {
      color: #2c3e50;
      font-weight: 600;
    }

    small {
      color: #6c757d;
    }
  }
}

.sticky-top {
  position: sticky;
  top: 0;
  z-index: 1000;
}
</style>
